import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './ConfirmModal.css';

/**
 * ConfirmModal Component
 * 
 * A styled confirmation modal with blurred/darkened backdrop.
 * Replaces browser's native confirm() dialog.
 */
const ConfirmModal = ({
  isOpen,
  title = 'Confirm',
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmVariant = 'danger', // 'danger' | 'primary' | 'warning'
  icon, // Optional custom icon
  onConfirm,
  onCancel,
  children, // Optional additional content (e.g., checkboxes)
  confirmDisabled = false,
}) => {
  const confirmBtnRef = useRef(null);

  // Auto-focus confirm button when modal opens
  useEffect(() => {
    if (isOpen && confirmBtnRef.current) {
      confirmBtnRef.current.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onCancel?.();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onCancel?.();
    } else if (e.key === 'Enter' && !confirmDisabled) {
      onConfirm?.();
    }
  };

  // Default icons based on variant
  const defaultIcons = {
    danger: '⚠',
    primary: '?',
    warning: '!',
  };
  const displayIcon = icon || defaultIcons[confirmVariant] || '?';
  const isReactIcon = React.isValidElement(displayIcon);

  const modal = (
    <div 
      className="confirm-modal__backdrop" 
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div className="confirm-modal__container" data-variant={confirmVariant}>
        <div className="confirm-modal__header">
          <div className="confirm-modal__icon-wrapper">
            <div className="confirm-modal__orbit" />
            <div className="confirm-modal__particle" />
            <div className="confirm-modal__particle confirm-modal__particle--secondary" />
            <div className={`confirm-modal__icon confirm-modal__icon--${confirmVariant}`}>
              {isReactIcon ? displayIcon : <span>{displayIcon}</span>}
            </div>
          </div>
          <h3 className="confirm-modal__title">{title}</h3>
        </div>
        
        <div className="confirm-modal__body">
          <p className="confirm-modal__message">{message}</p>
          {children}
        </div>
        
        <div className="confirm-modal__footer">
          {cancelLabel && (
            <button 
              className="confirm-modal__btn confirm-modal__btn--cancel"
              onClick={onCancel}
            >
              {cancelLabel}
            </button>
          )}
          <button 
            ref={confirmBtnRef}
            type="button"
            className={`confirm-modal__btn confirm-modal__btn--${confirmVariant}`}
            disabled={confirmDisabled}
            onClick={() => !confirmDisabled && onConfirm?.()}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default ConfirmModal;

